// src/utils/mediaTransform.js

/**
 * Image CDN prefix for optimization.
 * Set REACT_APP_DISABLE_GUMLET=true to bypass the CDN and serve images directly.
 */
export const GUMLET_PREFIX_URL = "https://images.wishlink.com/fetch/";
// Legacy Gumlet proxy prefix — still present in stored content and API responses
// during the migration to images.wishlink.com.
export const LEGACY_GUMLET_PREFIX_URL =
  "https://wishlink-proxy.gumlet.io/fetch/";
export const GUMLET_ENABLED = process.env.REACT_APP_DISABLE_GUMLET !== "true";
// Host of the image CDN. It serves `/fetch/<encoded origin>` URLs and also
// bucket keys directly (e.g. https://images.wishlink.com/creator-master/a.png).
export const IMAGE_CDN_HOST = "images.wishlink.com";

// images.wishlink.com is imgproxy, which serves images only: a video sent
// through it is a 422. Gumlet's proxy used to pass video through.
const VIDEO_EXTENSION_REGEX = /\.(mp4|m3u8|webm|mov)$/i;
const isVideoUrl = (url: string): boolean =>
  VIDEO_EXTENSION_REGEX.test(url.split(/[?#]/)[0]);

// A .lottie file is a zip archive, not an image, so imgproxy cannot serve it.
const isLottieUrl = (url: string): boolean =>
  /\.lottie$/i.test(url.split(/[?#]/)[0]);

// True when the URL is already served by the image CDN, with or without /fetch/.
const isImageCdnUrl = (url: string): boolean => {
  try {
    return new URL(url).hostname === IMAGE_CDN_HOST;
  } catch {
    return false;
  }
};

// Adds `w=<width>` to the URL unless it already has a `w` or `width` param.
function withWidth(url: string, width?: number): string {
  if (!width) return url;
  const hashIndex = url.indexOf("#");
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  if (/[?&](w|width)=/.test(base)) return url;
  return `${base}${base.includes("?") ? "&" : "?"}w=${width}${hash}`;
}

/**
 * Unwraps a proxied URL back to its origin URL. Returns the URL untouched when
 * it isn't proxied.
 */
function toOriginUrl(url: string): string {
  const matchedPrefix = url.startsWith(GUMLET_PREFIX_URL)
    ? GUMLET_PREFIX_URL
    : url.startsWith(LEGACY_GUMLET_PREFIX_URL)
    ? LEGACY_GUMLET_PREFIX_URL
    : null;
  if (!matchedPrefix) return url;

  const raw = url.slice(matchedPrefix.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    // Fallback: if the suffix isn't URI-encoded, return it as-is.
    return raw;
  }
}

/**
 * Transforms media URLs to use Gumlet CDN for optimization.
 *
 * Why encodeURIComponent?
 * Gumlet expects the origin URL to be passed as a single path segment after `/fetch/`.
 * `encodeURIComponent` safely encodes reserved characters like `: / ? & #` so querystrings
 * don't break the Gumlet fetch path.
 *
 * When Gumlet is disabled, we strip the prefix (if present) and decode it back.
 *
 * @param {string} url - The original media URL (or Gumlet-wrapped URL)
 * @returns {string} - The transformed URL with Gumlet prefix (if enabled), else the origin URL
 *
 * @example
 * transformMedia("https://cdn-alabaster.wishlink.com/image.jpg",  1080)
 * Returns: "https://images.wishlink.com/fetch/https%3A%2F%2Fcdn-alabaster.wishlink.com%2Fimage.jpg?w=1080"
 *
 * transformMedia("https://images.wishlink.com/creator-master/a.png", 300)
 * Returns: "https://images.wishlink.com/creator-master/a.png?w=300"
 */
export function transformMedia(url: string, width?: number): string {
  if (!url) return "";
  // Skip local URLs that shouldn't go through Gumlet:
  if (
    url.startsWith("blob:") ||
    url.startsWith("data:") ||
    url.startsWith("file:") ||
    url.startsWith("/") ||
    url.includes("cdn-cgi") ||
    url.includes("static5.lenskart.com")
  ) {
    return url;
  }

  // If Gumlet is disabled, return the original URL.
  if (!GUMLET_ENABLED) {
    // Strip existing CDN prefix if present and decode it back to origin URL.
    return toOriginUrl(url);
  }

  // Never wrap video with the image proxy.
  if (isVideoUrl(url)) return url;

  // Serve Lottie animations straight from the origin CDN.
  const originUrl = toOriginUrl(url);
  if (isLottieUrl(originUrl)) return originUrl;

  // Old Gumlet proxy URL: the path after /fetch/ works as is on
  // images.wishlink.com, so move it there instead of serving it from Gumlet.
  if (url.startsWith(LEGACY_GUMLET_PREFIX_URL)) {
    const moved = `${GUMLET_PREFIX_URL}${url.slice(
      LEGACY_GUMLET_PREFIX_URL.length
    )}`;
    return withWidth(moved, width);
  }

  // Already has the CDN prefix, return as-is.
  if (url.startsWith(GUMLET_PREFIX_URL)) return url;

  // A bucket key on the image CDN (no /fetch/). It is already optimized, so
  // only add the width when the caller asks for one.
  if (isImageCdnUrl(url)) return withWidth(url, width);

  const queryString = width ? `?w=${width}` : "";
  // Encode the origin URL so it can safely live inside the Gumlet `/fetch/` path segment.
  return `${GUMLET_PREFIX_URL}${encodeURIComponent(url)}${queryString}`;
}
